import { useState } from 'react';
import { Check, Copy, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCreateApiKey, type CreatedKey } from './org-api-queries';

export function CreateApiKeyDialog({ orgId }: { orgId: string | undefined }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [copied, setCopied] = useState(false);

  const createKey = useCreateApiKey(orgId);
  const canCreate = name.trim().length >= 1;
  // Native date input: min is tomorrow so a past/today date can't be picked.
  // Lazy state init keeps the Date.now() call out of the render body.
  const [tomorrow] = useState(() => new Date(Date.now() + 86_400_000).toISOString().slice(0, 10));

  function reset() {
    setName('');
    setExpiresAt('');
    setCreated(null);
    setCopied(false);
  }

  async function submit() {
    try {
      const key = await createKey.mutateAsync({
        name: name.trim(),
        ...(expiresAt ? { expiresAt } : {}),
      });
      setCreated(key);
    } catch {
      toast.error('Failed to create the key');
    }
  }

  async function copyToken() {
    if (!created) return;
    await navigator.clipboard.writeText(created.token);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger
        render={
          <Button>
            <Plus className="size-4" />
            Create API Key
          </Button>
        }
      />
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{created ? 'Your New Key' : 'Create API Key'}</DialogTitle>
        </DialogHeader>

        {created ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Input readOnly value={created.token} className="font-mono text-xs" />
              <Button variant="outline" size="icon" onClick={copyToken} aria-label="Copy key">
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <Button className="self-center" onClick={() => setOpen(false)}>Done</Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="key-name">Key name</Label>
              <Input
                id="key-name"
                value={name}
                maxLength={60}
                placeholder="Enter a name for this API key"
                onChange={(e) => setName(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Give your key a descriptive name to remember where it is used.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="key-expires">Expiration (optional)</Label>
              <Input
                id="key-expires"
                type="date"
                min={tomorrow}
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Set an expiration date for this key, or leave blank for no expiration.
              </p>
            </div>
            <Button disabled={!canCreate || createKey.isPending} onClick={submit}>
              {createKey.isPending && <Loader2 className="size-4 animate-spin" />}
              Create API Key
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
